import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, FileDown, FilePlus2, History, BellRing, Tablet } from "lucide-react";
import { ContractPreview } from "@/components/ContractPreview";
import { SendForSignature, openStudioSigning } from "@/components/SendForSignature";
import { VoidContract } from "@/components/VoidContract";
import { SignersStatus } from "@/components/SignersStatus";
import { toast } from "sonner";
import { api, downloadFile, errMsg, fmtDateTime } from "@/lib/api";
import { PageHeader, Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { ContractEditor } from "@/components/ContractEditor";

const ST = { bozza: "Bozza", inviato: "Inviato in firma", firmato: "Firmato", rifiutato: "Rifiutato dal cliente", annullato: "Annullato" };
const DS_ST = { sent: "inviata", delivered: "aperta", completed: "completata", declined: "rifiutata", voided: "annullata" };

function Versions({ c, onLoad, editable }) {
  return (
    <Card className="p-4" data-testid="contract-versions">
      <p className="font-heading font-semibold text-sm text-slate-900 flex items-center gap-2 mb-2"><History className="h-4 w-4" />Storico versioni</p>
      <ul className="space-y-1 max-h-72 overflow-y-auto">
        {c.versions.map((v) => (
          <li key={v.version} className="text-xs flex items-center gap-2">
            <span className="font-medium shrink-0">v{v.version}</span><span className="text-slate-500 flex-1 min-w-0 truncate" title={v.saved_by}>{fmtDateTime(v.saved_at)} · {v.saved_by}</span>
            {editable && v.version !== c.version && <button className="text-sky-700 hover:underline shrink-0" onClick={() => onLoad(v.version)} data-testid={`contract-version-load-${v.version}`}>carica</button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Conversion({ c, onRetry, busy }) {
  const done = c.conversion === "completata";
  return (
    <Card className="p-4 text-xs text-slate-600 space-y-2" data-testid="contract-conversion">
      <p className="font-heading font-semibold text-sm text-slate-900">Conversione in cliente</p>
      {done ? (
        <>
          <p data-testid="contract-conversion-done">Anagrafica e Trust creati automaticamente il {fmtDateTime(c.converted_at)}.</p>
          <Link to={`/trusts/${c.converted_trust_id}`} className="text-sky-700 hover:underline block" data-testid="contract-open-trust">Apri il Trust (bozza)</Link>
          <Link to="/subjects" className="text-sky-700 hover:underline block" data-testid="contract-open-subjects">Vai alle Anagrafiche</Link>
        </>
      ) : (
        <>
          <p data-testid="contract-conversion-pending">{c.conversion === "errore" ? `Conversione non riuscita: ${c.conversion_error || ""}` : "Conversione in corso…"}</p>
          <Button size="sm" variant="outline" disabled={busy} onClick={onRetry} data-testid="contract-convert-retry">Crea anagrafica e Trust</Button>
        </>
      )}
    </Card>
  );
}

export default function ContractPage() {
  const { pid } = useParams();
  const [prospect, setProspect] = useState(null);
  const [c, setC] = useState(undefined);
  const [html, setHtml] = useState("");
  const [base, setBase] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, list] = await Promise.all([api.get(`/e/prospects/${pid}`), api.get("/contracts", { params: { prospect_id: pid } })]);
    setProspect(p.data);
    if (!list.data.length) return setC(null);
    const { data } = await api.get(`/contracts/${list.data[0].id}`);
    setC(data); setHtml(data.html); setBase(data.html);
  }, [pid]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const create = () => run(async () => { await api.post("/contracts", { prospect_id: pid }); toast.success("Contratto creato dal modello"); await load(); });
  const save = () => run(async () => { const { data } = await api.put(`/contracts/${c.id}`, { html }); toast.success(`Salvata versione ${data.version}`); await load(); });
  const loadVersion = (v) => run(async () => { const { data } = await api.get(`/contracts/${c.id}/versions/${v}`); setBase(data.html); setHtml(data.html); toast(`Caricata la versione ${v}: premi Salva per ripristinarla`); });
  const remind = () => run(async () => { const { data } = await api.post(`/contracts/${c.id}/remind`); toast.success(`Promemoria DocuSign rinviato a ${data.name || data.to}`); await load(); });
  const retry = () => run(async () => { await api.post(`/contracts/${c.id}/convert`); toast.success("Anagrafica e Trust creati"); await load(); });

  if (c === undefined) return null;
  const name = prospect ? `${prospect.first_name || ""} ${prospect.last_name || ""}`.trim() : "";
  const dirty = c && html !== c.html;
  const editable = ["bozza", "rifiutato", "annullato"].includes(c?.status);
  const remindLocked = c?.last_remind_at && Date.now() - new Date(c.last_remind_at).getTime() < 864e5;
  return (
    <div data-testid="contract-page">
      <Link to="/prospects" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3" data-testid="contract-back"><ArrowLeft className="h-4 w-4" />Nuovi Clienti</Link>
      <PageHeader eyebrow="Mandato di consulenza Trust" title={name || "Contratto"}
        subtitle={c ? `${ST[c.status]} · versione ${c.version} · ultima modifica ${fmtDateTime(c.updated_at)} (${c.updated_by})` : "Nessun contratto ancora creato per questo cliente."}>
        {c && (
          <div className="flex flex-wrap gap-2">
            {editable && <Button variant="outline" disabled={busy || !dirty} onClick={save} data-testid="contract-save"><Save className="h-4 w-4 mr-1" />Salva</Button>}
            <ContractPreview cid={c.id} version={c.version} name={name} disabled={busy || dirty} />
            {c.status === "inviato" && <Button variant="outline" disabled={busy || remindLocked} onClick={remind} title={remindLocked ? "Promemoria già inviato nelle ultime 24 ore" : ""} data-testid="contract-remind"><BellRing className="h-4 w-4 mr-1" />Rimanda promemoria</Button>}
            <Button variant="outline" disabled={busy || dirty} onClick={() => downloadFile(`/contracts/${c.id}/export?format=docx`, `Contratto_${name}.docx`)} data-testid="contract-export-docx"><FileDown className="h-4 w-4 mr-1" />Word</Button>
            <Button variant="outline" disabled={busy || dirty} onClick={() => downloadFile(`/contracts/${c.id}/export?format=pdf`, `Contratto_${name}.pdf`)} data-testid="contract-export-pdf"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
            {c.status === "bozza" && <SendForSignature c={c} prospect={prospect} disabled={busy || dirty} onDone={load} />}
            {c.status === "inviato" && c.sign_mode === "studio" && <Button disabled={busy} onClick={() => run(() => openStudioSigning(c.id))} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="contract-studio-open"><Tablet className="h-4 w-4 mr-1" />Apri firma sul tablet</Button>}
            {c.status === "inviato" && <VoidContract cid={c.id} disabled={busy} onDone={load} />}
            {c.status === "firmato" && <Button onClick={() => downloadFile(`/contracts/${c.id}/signed`, `Contratto_firmato_${name}.pdf`)} className="bg-emerald-700 text-white" data-testid="contract-download-signed"><FileDown className="h-4 w-4 mr-1" />Contratto firmato</Button>}
          </div>
        )}
      </PageHeader>
      {!c ? (
        <Card className="p-8 max-w-lg" data-testid="contract-empty">
          <p className="text-sm text-slate-600 mb-4">Il contratto verrà creato dal modello “Consulenza Trust Mandato Standard”, già compilato con i dati della scheda del cliente.</p>
          <Button onClick={create} disabled={busy} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="contract-create"><FilePlus2 className="h-4 w-4 mr-1" />Crea contratto dal modello</Button>
        </Card>
      ) : (
        <div className="grid xl:grid-cols-[1fr_280px] gap-6">
          <ContractEditor html={base} editable={editable} onChange={setHtml} />
          <div className="space-y-4">
            <Card className="p-4 text-xs text-slate-600 space-y-1" data-testid="contract-sign-info">
              <p className="font-heading font-semibold text-sm text-slate-900">Firma DocuSign</p>
              <p>1. Cliente “Per accettazione”: {prospect?.email || <span className="text-red-600">email mancante</span>}</p>
              <p>2. Bruno Benvenuti “Per convalida”</p>
              {c.signature_request_id && <SignersStatus cid={c.id} active={c.status === "inviato"} />}
              {c.signature_status && c.status !== "bozza" && <p>Stato DocuSign: <b>{DS_ST[c.signature_status] || c.signature_status}</b></p>}
              {c.last_remind_at && <p data-testid="contract-last-remind">Ultimo promemoria: {fmtDateTime(c.last_remind_at)} a {c.last_remind_to} ({c.last_remind_by})</p>}
              {c.sign_mode === "studio" && c.id_check && <p data-testid="contract-id-check">Firma in studio · documento verificato da {c.id_check.by} il {fmtDateTime(c.id_check.at)}{c.id_check.doc ? ` (${c.id_check.doc})` : ""}</p>}
            </Card>
            {c.status === "annullato" && c.void_reason && (
              <Card className="p-4 text-xs border-amber-200 bg-amber-50/60 space-y-1" data-testid="contract-voided">
                <p className="font-heading font-semibold text-sm text-amber-900">Ritirato dalla firma</p>
                <p className="text-slate-700">{c.voided_by} · {fmtDateTime(c.voided_at)}</p>
                <p className="text-slate-700" data-testid="contract-void-reason">Motivo: <i>{c.void_reason}</i></p>
                <p className="text-slate-600">Correggi il testo e salva: il contratto torna in bozza come nuova versione e può essere rinviato.</p>
              </Card>
            )}
            {c.status === "rifiutato" && (
              <Card className="p-4 text-xs border-red-200 bg-red-50/60 space-y-1" data-testid="contract-declined">
                <p className="font-heading font-semibold text-sm text-red-800">Firma rifiutata</p>
                <p className="text-slate-700">{c.declined_by || "Il firmatario"} · {fmtDateTime(c.declined_at)}</p>
                <p className="text-slate-700" data-testid="contract-decline-reason">Motivo: <i>{c.decline_reason || "nessun motivo indicato"}</i></p>
                <p className="text-slate-600">Correggi il testo e salva: il contratto torna in bozza come nuova versione e può essere rinviato.</p>
              </Card>
            )}
            {c.status === "firmato" && <Conversion c={c} onRetry={retry} busy={busy} />}
            <Versions c={c} onLoad={loadVersion} editable={editable} />
          </div>
        </div>
      )}
    </div>
  );
}
